import { ShieldAlert } from 'lucide-react';
import { TalkToTeamButton } from './learner/TalkToTeamButton';
import { useAuth } from '../lib/auth-context';


/**
 * V14.5 — Non-blocking suspension banner for learners.
 *
 * Renders at the top of every learner page when `user.is_suspended === true`.
 * The user has been auto-downgraded to the Free tier and can still browse
 * the portal as a free user — they just can't book live classes or access
 * paid on-demand courses (free-tier gates handle that naturally). This
 * banner tells them what happened and how to contact our team if it's a
 * mistake (common case: postnatal delivery or renewal lapse).
 */
export default function SuspendedBanner() {
  const { user } = useAuth();
  if (!user?.is_suspended) return null;

  return (
    <div
      className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 sm:px-6"
      data-testid="suspended-banner"
      role="alert"
    >
      <div className="max-w-7xl mx-auto flex items-start gap-3 text-sm">
        <ShieldAlert className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-amber-900 leading-snug">
            <span className="font-semibold">Your account is suspended.</span>{' '}
            You can still browse as a free user, but you can&apos;t book live
            classes or access paid on-demand courses. If you think this is a
            mistake, please contact our team to reactivate.
          </p>
          <TalkToTeamButton category="support" message="My account shows as suspended — please review and reactivate if this is a mistake."
            label="Request a callback" testId="suspended-banner-callback-btn" className="mt-1" />
        </div>
      </div>
    </div>
  );
}
